import axios from 'axios';
import { trackEvent } from '../utils/analytics';

const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
const cache = new Map(); // key -> { timestamp, data }
const BRAND_LOGO_PATH = '/tph-logo-desktop.png';
const LEGACY_LOGO_BASENAMES = new Set([
  'rst-logo.webp',
  'rst-logo.png',
  'rst-logo-320x120-darkbg.png',
  'rst-favicon-32x32.webp',
  'rst-favicon-32x32.png',
  'tph_logo.png',
  'tph-logo.png',
]);

function normalizeLegacyLogoPath(path) {
  if (typeof path !== 'string' || !path.trim()) return path;
  const sanitized = path.trim().split(/[?#]/)[0];
  const filename = sanitized.split('/').pop()?.toLowerCase();
  if (!filename) return path;
  return LEGACY_LOGO_BASENAMES.has(filename) ? BRAND_LOGO_PATH : path;
}

function makeKey(locationId) {
  return `ads:${locationId || 'all'}`;
}

export async function fetchAds(locationId, { force = false } = {}) {
  const key = makeKey(locationId);
  const now = Date.now();
  const cached = cache.get(key);

  if (!force && cached && now - cached.timestamp < CACHE_TTL_MS) {
    return cached.data;
  }

  try {
    const params = locationId ? { locationId } : {};
    const { data } = await axios.get('/api/ads', { params });
    const ads = Array.isArray(data?.ads)
      ? data.ads.map((ad) => ({
          ...ad,
          imageSrc: normalizeLegacyLogoPath(ad?.imageSrc),
        }))
      : [];
    cache.set(key, { timestamp: now, data: ads });
    return ads;
  } catch (err) {
    // On error, return stale cache if present
    if (cached) return cached.data;
    throw err;
  }
}

export function clearAdsCache(locationId) {
  if (locationId) cache.delete(makeKey(locationId));
  else cache.clear();
}

export async function logAdEvent(type, metadata = {}) {
  // Block only if REACT_APP_DISABLE_ANALYTICS is 'true'
  const isDisabled = process.env.REACT_APP_DISABLE_ANALYTICS === 'true';
  
  if (isDisabled) {
    return;
  }

  try {
    trackEvent(type, {
      source: 'ad_slider',
      ...metadata,
    });
  } catch (_e) {
    // ignore
  }
}
